import { ErrorCode, ErrorName, ErrorType, PlayerError, } from '@tunein/web-media-player';
import { forEach } from 'es-toolkit/compat';
import { player } from '../../singletons';
import { ClientSideAdReporter } from '../ClientSideAdReporter';
import { ServerSidePrerollReporter } from '../ServerSidePrerollReporter';
export class AdReporter {
    #ssaiManager; // TODO: implement SSAI reporting
    #serverSidePrerollReporter;
    #clientSideAdReporter;
    #events;
    #isServerSidePreroll = false;
    constructor(options) {
        const { ssaiManager } = options;
        this.#serverSidePrerollReporter = new ServerSidePrerollReporter();
        this.#clientSideAdReporter = new ClientSideAdReporter();
        this.#ssaiManager = ssaiManager;
        this.#events = {
            // client-side preroll events
            adloaded: this.#onAdLoaded,
            aderror: this.#onAdError,
            adstart: this.#onAdStart,
            adresume: this.#onAdResume,
            adpause: this.#onAdPause,
            adcomplete: this.#onAdComplete,
            adskipped: this.#onAdSkipped,
            adclick: this.#onAdClick,
            // server-side preroll events
            playing: this.#onPlaying,
            pause: this.#onPause,
            ended: this.#onEnded,
            error: this.#onError,
        };
    }
    #onAdLoaded = (adEvent) => {
        this.#clientSideAdReporter.reportAdLoaded(adEvent);
    };
    #onAdError = (adErrorEvent) => {
        this.#clientSideAdReporter.reportError(adErrorEvent);
    };
    #onAdStart = (adEvent) => {
        this.#clientSideAdReporter.reportAdStart(adEvent);
    };
    #onAdResume = (adEvent) => {
        this.#clientSideAdReporter.reportAdResume(adEvent);
    };
    #onAdPause = (adEvent) => {
        this.#clientSideAdReporter.reportAdPause(adEvent);
    };
    #onAdComplete = () => {
        this.#clientSideAdReporter.reportAdComplete();
    };
    reportAdBreakCompleted = () => {
        if (this.#isServerSidePreroll) {
            this.#serverSidePrerollReporter.reportAdBreakCompleted();
        }
        else {
            this.#clientSideAdReporter.reportAdBreakCompleted();
        }
    };
    #onAdSkipped = () => {
        this.#clientSideAdReporter.onAdSkipped();
    };
    #onAdClick = () => {
        this.#clientSideAdReporter.reportAdClick();
    };
    #onPlaying = () => {
        if (this.#isServerSidePreroll) {
            this.#serverSidePrerollReporter.reportPlaying();
        }
    };
    #onPause = () => {
        const duration = player.getDuration();
        const currentTime = player.getCurrentTime();
        // When an on-demand stream reaches its end, the browser will emit a paused event. We only want to report on a
        // user-initiated pause, so we'll exit early here when the current time is equal to the duration.
        if (currentTime === duration) {
            return;
        }
        if (this.#isServerSidePreroll) {
            this.#serverSidePrerollReporter.reportPaused();
        }
    };
    #onEnded = () => {
        if (this.#isServerSidePreroll) {
            this.#serverSidePrerollReporter.reportFinished();
        }
    };
    #onError = (event) => {
        if (this.#isServerSidePreroll && event.error?.fatal && event.isLastSource) {
            this.#serverSidePrerollReporter.reportFailed({ error: event.error });
        }
    };
    registerEvents() {
        forEach(this.#events, (cb, event) => player.on(event, cb));
    }
    deregisterEvents() {
        forEach(this.#events, (cb, event) => player.off(event, cb));
    }
    onAdRequested(sessionMetadata) {
        this.#clientSideAdReporter.sessionMetadata = sessionMetadata;
    }
    //==== Client-side preroll ====
    reportAudioVideoPrerollEligibility(eligibilityParameters) {
        this.#clientSideAdReporter.reportEligibility(eligibilityParameters);
    }
    reportAudioVideoAdsBiddingRequested = () => {
        this.#clientSideAdReporter.reportAdsBiddingRequested();
    };
    reportAudioVideoAdsBiddingResponseReceived = (bids) => {
        if (bids?.length) {
            this.#clientSideAdReporter.reportAdsBiddingResponseReceived();
            return;
        }
        this.#clientSideAdReporter.reportAdsBiddingRequestFailed('87', 'No parameters received.');
    };
    reportAudioVideoAdsBiddingRequestFailed = () => {
        this.#clientSideAdReporter.reportAdsBiddingRequestFailed('85', 'Amazon keywords request failed.');
    };
    reportAudioVideoAdRequested(hasApsKeywords = false) {
        this.#clientSideAdReporter.reportAdRequested(hasApsKeywords);
    }
    reportAdError(error) {
        this.#clientSideAdReporter.reportError(error);
    }
    reportNoFill() {
        const noFillError = new PlayerError('Ad no fill error', ErrorCode.AdNoFillError, ErrorType.PLAY_ERROR, ErrorName.AdError, false);
        this.#clientSideAdReporter.reportError(noFillError);
    }
    reportNoAdConfig() {
        const noConfigError = new PlayerError('Ad no config error', ErrorCode.AdNoConfigError, ErrorType.LOAD_ERROR, ErrorName.AdError, false);
        this.#clientSideAdReporter.reportError(noConfigError);
    }
    //==== Server-side preroll ====
    reportServerSidePrerollEligibility(prerollStream) {
        this.#isServerSidePreroll = !!prerollStream;
        this.#serverSidePrerollReporter.reportEligibility(this.#isServerSidePreroll, prerollStream?.bitrate);
    }
    reportServerSidePrerollRequested() {
        this.#serverSidePrerollReporter.reportRequested();
    }
    reportServerSidePrerollResponseReceived() {
        this.#serverSidePrerollReporter.reportResponseReceived();
    }
}
